import * as React from 'react';
import { JRCIconButton } from '../JRCIconButton/JRCIconButton';
import styled from 'styled-components';
import classnames from 'classnames';
import { DataCy } from '../../types/dataAttributes';

const PAGINATION_RANGE_SIDE = 2;

export type PaginationBulletProps = DataCy & {
    number: number;
    isActive: boolean;
    clickHandler: (page: number) => void;
};
/**
 * Props type for JRCPagination
 * @member activePage number :  active page Number
 * @member nbrOfPage number : total nb of pages
 * @member clickHandler (page: number) => void; callback when click on a page number
 */
export type JRCPaginationProps = DataCy & {
    activePage: number;
    nbrOfPage: number;
    clickHandler: (page: number) => void;
};

const StyledBullet = styled.button`
    cursor: pointer;
    min-width: 8px;
    text-align: center;
    padding: 5px 8px;
    border-radius: 4px;
    &.active {
        padding: 5px 8px 3px;
        color: ${(props) => props.theme.color.overseas};
        font-weight: 500;
        &:after {
            content: '';
            height: 2px;
            background: ${(props) => props.theme.color.overseas};
            border-radius: 1px;
            display: block;
        }
    }
    &:hover {
        padding: 5px 8px 3px;
        &:after {
            content: '';
            height: 2px;
            background: ${(props) => props.theme.color.overseas};
            border-radius: 1px;
            display: block;
        }
    }
    &:focus-visible {
        background-color: ${(props) => props.theme.color.grey2};
    }
`;

export const PaginationBullet = ({ number, isActive, dataCy, clickHandler }: PaginationBulletProps) => {
    return (
        <StyledBullet
            type={'button'}
            className={classnames({
                active: isActive,
            })}
            data-cy={dataCy && `${dataCy}-page-${number}`}
            onClick={() => !isActive && clickHandler(number)}>
            {number}
        </StyledBullet>
    );
};

const PaginationBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;
const PaginationNumber = styled.div`
    text-align: center;
    display: flex;
`;

const Ellipsis = styled.span`
    margin-top: 6px;
`;

const getBulletArray = (
    begin: number,
    end: number,
    activePage: number,
    clickHandler: (number: number) => void,
    dataCy?: string,
) => {
    const toRender = [];
    for (let i = begin; i <= end; i++) {
        toRender.push(
            <PaginationBullet
                key={i}
                number={i}
                isActive={i === activePage}
                dataCy={dataCy}
                clickHandler={clickHandler}
            />,
        );
    }
    return toRender;
};

export const JRCPagination = ({ activePage = 1, nbrOfPage, clickHandler, dataCy }: JRCPaginationProps) => {
    let toRender = [];
    const RANGE = 2 * PAGINATION_RANGE_SIDE + 1;

    toRender.push(
        <PaginationBullet key={1} number={1} isActive={1 === activePage} dataCy={dataCy} clickHandler={clickHandler} />,
    );

    if (nbrOfPage >= RANGE + 2 && activePage >= RANGE) {
        toRender.push(<Ellipsis key="...start">...</Ellipsis>);
    }

    const begin = Math.max(2, Math.min(nbrOfPage - RANGE + 1, activePage - PAGINATION_RANGE_SIDE));
    const end = Math.min(nbrOfPage - 1, Math.max(RANGE, activePage + PAGINATION_RANGE_SIDE));
    toRender = [...toRender, ...getBulletArray(begin, end, activePage, clickHandler, dataCy)];

    if (nbrOfPage >= RANGE + 2 && activePage <= nbrOfPage - RANGE + 1) {
        toRender.push(<Ellipsis key="...end">...</Ellipsis>);
    }

    if (nbrOfPage > 1) {
        toRender.push(
            <PaginationBullet
                key={nbrOfPage}
                number={nbrOfPage}
                isActive={nbrOfPage === activePage}
                dataCy={dataCy}
                clickHandler={clickHandler}
            />,
        );
    }

    return (
        <PaginationBox>
            <JRCIconButton
                data-testid="previous"
                disabled={activePage === 1}
                icon="icon-chevron-left"
                dataCy={dataCy && `${dataCy}-page-previous`}
                onClick={() => clickHandler(activePage - 1)}
            />
            <PaginationNumber>{toRender}</PaginationNumber>
            <JRCIconButton
                data-testid="next"
                disabled={activePage === nbrOfPage}
                icon="icon-chevron-right"
                dataCy={dataCy && `${dataCy}-page-next`}
                onClick={() => clickHandler(activePage + 1)}
            />
        </PaginationBox>
    );
};
